import { useState, useEffect } from 'react';
import { TextInput, PasswordInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconKey, IconMail, IconLock } from '@tabler/icons-react';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import { useNavigate } from 'react-router-dom';
import { clientRoutes } from '@/ApiPaths';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import {
  AuthPageShell,
  AuthSubmitButton,
  AuthSecondaryButton,
} from '@/components/auth/AuthPageShell';
import { getAuthInputStyles } from '@/components/auth/authInputStyles';
import authClasses from '@/components/auth/AuthPage.module.css';

export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasToken, setHasToken] = useState<boolean | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const accentColor = DEFAULT_QR_CODE_COLOR;
  const inputStyles = getAuthInputStyles(accentColor);

  const emailForm = useForm({
    initialValues: { email: '' },
    validate: {
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : t('login.invalidEmail'),
    },
  });

  const passwordForm = useForm({
    initialValues: { password: '', confirmPassword: '' },
    validate: {
      password: (value) => (value.length >= 6 ? null : t('resetPassword.passwordMinLength')),
      confirmPassword: (value, values) =>
        value === values.password ? null : t('resetPassword.passwordMismatch'),
    },
  });

  useEffect(() => {
    const token = EmailPassword.getResetPasswordTokenFromURL();
    setHasToken(!!token);
  }, []);

  const handleSendResetEmail = async (values: { email: string }) => {
    setIsSubmitting(true);
    try {
      const response = await EmailPassword.sendPasswordResetEmail({
        formFields: [{ id: 'email', value: values.email }],
      });
      if (response.status === 'OK') {
        setEmailSent(true);
        notifications.show({
          title: t('resetPassword.emailSent'),
          message: '',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else if (response.status === 'PASSWORD_RESET_NOT_ALLOWED') {
        notifications.show({
          title: t('resetPassword.notAllowed'),
          message: '',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
      } else if (response.status === 'FIELD_ERROR' && response.formFields) {
        const msg = response.formFields.map((f) => f.error).join(' ');
        notifications.show({ title: msg, message: '', color: 'red', icon: <IconAlertCircle size={16} /> });
      }
    } catch {
      notifications.show({
        title: t('resetPassword.sendError'),
        message: '',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitNewPassword = async (values: { password: string }) => {
    setIsSubmitting(true);
    try {
      const response = await EmailPassword.submitNewPassword({
        formFields: [{ id: 'password', value: values.password }],
      });
      if (response.status === 'OK') {
        setResetSuccess(true);
        notifications.show({
          title: t('resetPassword.resetSuccess'),
          message: '',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
        setTimeout(() => navigate(clientRoutes.login), 2000);
      } else if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
        notifications.show({
          title: t('resetPassword.invalidToken'),
          message: '',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
      } else if (response.status === 'FIELD_ERROR' && response.formFields) {
        const msg = response.formFields.map((f) => f.error).join(' ');
        notifications.show({ title: msg, message: '', color: 'red', icon: <IconAlertCircle size={16} /> });
      }
    } catch {
      notifications.show({
        title: t('resetPassword.submitError'),
        message: '',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (hasToken === null) {
    return null;
  }

  const footer = (
    <AuthSecondaryButton
      onClick={() => navigate(clientRoutes.login)}
      accentColor={accentColor}
    >
      {t('resetPassword.backToLogin')}
    </AuthSecondaryButton>
  );

  return (
    <AuthPageShell
      title={hasToken ? t('resetPassword.newPasswordTitle') : t('resetPassword.title')}
      subtitle={
        hasToken ? t('resetPassword.newPasswordSubtitle') : t('resetPassword.subtitle')
      }
      icon={<IconKey size={22} stroke={1.75} />}
      accentColor={accentColor}
      footer={footer}
    >
      {!hasToken && emailSent ? (
        <p className={authClasses.statusMessage}>{t('resetPassword.checkEmail')}</p>
      ) : null}

      {!hasToken && !emailSent ? (
        <form onSubmit={emailForm.onSubmit(handleSendResetEmail)} className={authClasses.formStack}>
          <TextInput
            label={t('login.email')}
            placeholder={t('login.emailPlaceholder')}
            leftSection={<IconMail size={17} />}
            size="md"
            radius="md"
            styles={inputStyles}
            {...emailForm.getInputProps('email')}
            required
          />
          <AuthSubmitButton loading={isSubmitting} accentColor={accentColor}>
            {t('resetPassword.sendResetLink')}
          </AuthSubmitButton>
        </form>
      ) : null}

      {hasToken && !resetSuccess ? (
        <form
          onSubmit={passwordForm.onSubmit(handleSubmitNewPassword)}
          className={authClasses.formStack}
        >
          <PasswordInput
            label={t('resetPassword.newPassword')}
            placeholder={t('resetPassword.newPasswordPlaceholder')}
            leftSection={<IconLock size={17} />}
            size="md"
            radius="md"
            styles={inputStyles}
            {...passwordForm.getInputProps('password')}
            required
          />
          <PasswordInput
            label={t('resetPassword.confirmPassword')}
            placeholder={t('resetPassword.confirmPasswordPlaceholder')}
            leftSection={<IconLock size={17} />}
            size="md"
            radius="md"
            styles={inputStyles}
            {...passwordForm.getInputProps('confirmPassword')}
            required
          />
          <AuthSubmitButton loading={isSubmitting} accentColor={accentColor}>
            {t('resetPassword.submitNewPassword')}
          </AuthSubmitButton>
        </form>
      ) : null}

      {hasToken && resetSuccess ? (
        <p className={authClasses.statusMessage}>{t('resetPassword.redirectToLogin')}</p>
      ) : null}
    </AuthPageShell>
  );
}
